import React, { useMemo } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { Frame } from '@/types/location';
import { PreloadedSequence } from '@/types/almanac';
import { useAlmanac } from '@/hooks/useAlmanac';
import Overlay from './Overlay';
import Controls from './Controls';

export interface PlayerProps {
  cachedFrame?: HTMLImageElement;
  cachedSequence?: PreloadedSequence;
  isPlaying: boolean;
  stop: (updateState?: boolean) => void;
  play: () => void;
  handleSkip: (direction: 'back' | 'forward') => void;
  onDotClick: (index: number) => void;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  showDateAndDotMenu: boolean;
}

export default function Player({
  cachedFrame,
  cachedSequence,
  isPlaying,
  stop,
  play,
  handleSkip,
  onDotClick,
  showModal,
  setShowModal,
  showDateAndDotMenu,
}: PlayerProps) {
  const { sortedSequences, activeSequenceIndex, activeFrameIndex } =
    useAlmanac();
  const activeFrame: Frame | undefined =
    sortedSequences[activeSequenceIndex]?.[activeFrameIndex];
  const isLoading = useMemo(() => {
    return !activeFrame;
  }, [activeFrame]);
  return (
    <div className="group relative flex w-full">
      {!showModal && !isLoading && (
        <Overlay
          setShowModal={setShowModal}
          onDotClick={onDotClick}
          showDateAndDotMenu={showDateAndDotMenu}
        />
      )}
      <Controls
        status={cachedSequence?.status}
        isPlaying={isPlaying}
        isFirst={activeSequenceIndex === 0}
        isLast={activeSequenceIndex === sortedSequences.length - 1}
        skipCallback={handleSkip}
        pauseCallback={stop}
        playCallback={play}
      />
      {!showModal ? (
        <div className="relative w-full overflow-hidden rounded-md">
          <div className="aspect-h-1 aspect-w-2 bg-neutral-1000">
            {cachedSequence?.status === 'ready' ? (
              cachedFrame?.src && (
                <img
                  className="size-full select-none object-cover"
                  src={cachedFrame?.src}
                  alt={`Active frame}`}
                />
              )
            ) : (
              <>
                {cachedSequence?.images[0]?.src ? (
                  <img
                    className="size-full select-none object-cover"
                    src={cachedSequence?.images[0]?.src}
                    alt={`Active frame}`}
                  />
                ) : (
                  <div className="size-full select-none" />
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-dvh w-dvw items-center justify-center">
          <TransformWrapper centerOnInit={true}>
            <TransformComponent>
              <img
                className="h-screen max-h-dvh max-w-max"
                style={{ aspectRatio: '16/9' }}
                src={
                  sortedSequences[activeSequenceIndex]?.[activeFrameIndex]?.url
                }
                alt="Active frame"
              />
            </TransformComponent>
          </TransformWrapper>
        </div>
      )}
    </div>
  );
}
